import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";

import type { AgencyClientSchemaType } from "../schema";
import type { AgencyClient } from "../types";

async function addAgencyClientRequest(
  workspaceId: string,
  newClient: AgencyClientSchemaType,
): Promise<AgencyClient> {
  const { firstName, lastName, email } = newClient;
  return (
    await post(`workspaces/${workspaceId}/clients`, {
      first_name: firstName,
      last_name: lastName,
      email,
    })
  ).client;
}

export default function useAgencyClientsMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { agencyClientsKeys } = getQueryKeys(workspaceId);

  const {
    mutateAsync: addAgencyClient,
    isPending: isAddingAgencyClient,
    error: addAgencyClientError,
    reset: resetAgencyClientError,
  } = useMutation({
    mutationFn: ({ newClient }: { newClient: AgencyClientSchemaType }) =>
      addAgencyClientRequest(workspaceId, newClient),
    onSuccess: (newClient) => {
      queryClient.setQueryData<AgencyClient[]>(
        agencyClientsKeys.list,
        (prevClients) => [newClient, ...prevClients],
      );
    },
  });

  return {
    addAgencyClient,
    isAddingAgencyClient,
    addAgencyClientError,
    resetAgencyClientError,
  };
}
